import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import FilmCard from '@/components/base/FilmCard';
import EmptyState from '@/components/base/EmptyState';
import { films } from '@/mocks/films';
import { formatDate, formatDuration, getCategoryById, getFilmById, getRecommended } from '@/utils/content';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function DetailPage() {
  const { contentId = '' } = useParams();
  const film = getFilmById(contentId);
  const { isFavorite, toggle } = useFavorites();
  const [toast, setToast] = useState('');

  usePageMeta({
    title: film ? `${film.title} - 成人影视详情` : '内容不存在 - 成人影视',
    description: film
      ? `${film.summary} 影片简介与相关资料，附同类型成人影视推荐。导演 ${film.director}，${film.year} 年作品。`
      : '该影视条目不存在或已被移除，可返回首页浏览其他内容。',
    keywords: film ? `成人影视,${film.title},${film.region}影视,${film.subcategory}` : '成人影视,影视详情',
  });

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const handleShare = async () => {
    if (!film) return;
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setToast('页面链接已复制，可分享给朋友');
      } else {
        setToast('当前浏览器不支持自动复制，请手动复制地址栏链接');
      }
    } catch {
      setToast('复制失败，请手动复制地址栏链接');
    }
  };

  if (!film) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="page-shell py-20">
          <EmptyState
            icon="ri-file-unknow-line"
            title="内容不存在"
            description="该影视条目可能已被移除，或链接地址不正确。可以返回首页继续浏览其他条目。"
            primaryAction={{ label: '返回首页', to: '/' }}
            secondaryAction={{ label: '搜索其他内容', to: '/search' }}
          />
          <div className="mt-12">
            <p className="eyebrow mb-4">也可以看看这些</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-5">
              {films.slice(0, 5).map((item, index) => (
                <FilmCard key={item.id} film={item} index={index} />
              ))}
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const category = getCategoryById(film.category);
  const recommended = getRecommended(film, 4);
  const favorite = isFavorite(film.id);

  const metaRows = [
    { label: '导演', value: film.director },
    { label: '主演', value: film.cast.join(' / ') },
    { label: '类型', value: `${category?.name ?? '未分类'} · ${film.subcategory}` },
    { label: '地区', value: film.region },
    { label: '年份', value: `${film.year} 年` },
    { label: '片长', value: formatDuration(film.duration) },
    { label: '收录时间', value: formatDate(film.publishedAt) },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <div className="page-shell pt-6">
          <nav className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-400">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${film.category}`} className="transition-colors hover:text-primary-400">
              {category?.name ?? '分类'}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{film.title}</span>
          </nav>
        </div>

        <article className="page-shell pt-6">
          <div className="grid gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-4">
              <div className="sticky top-24">
                <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-300/70 bg-background-200">
                  <img
                    src={film.cover}
                    alt={`${film.title} 影片主封面`}
                    title={`${film.title} · 成人影视详情`}
                    className="h-full w-full object-cover object-top"
                  />
                  <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-md bg-background-50/85 px-2 py-1 font-label text-[11px] text-primary-400 backdrop-blur-sm">
                    <i className="ri-star-fill text-[10px]" />
                    {film.rating.toFixed(1)}
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      toggle(film.id);
                      setToast(favorite ? '已取消收藏' : '已加入本地收藏');
                    }}
                    className={`flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border py-2.5 text-[12px] transition-colors ${
                      favorite
                        ? 'border-primary-500/60 bg-primary-500/10 text-primary-400'
                        : 'border-background-300 text-foreground-700 hover:border-primary-500/60 hover:text-primary-400'
                    }`}
                  >
                    <i className={favorite ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
                    {favorite ? '已收藏' : '收藏'}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-[12px] text-foreground-700 transition-colors hover:border-primary-500/60 hover:text-primary-400"
                  >
                    <i className="ri-share-forward-line" />
                    分享
                  </button>
                </div>

                <p className="mt-3 rounded-md border border-background-300/60 bg-background-100 px-3 py-2.5 text-[11px] leading-5 text-foreground-500">
                  <i className="ri-information-line mr-1 text-primary-400" />
                  收藏状态保存在本机浏览器，仅用于功能演示。本站不提供播放与下载。
                </p>
              </div>
            </div>

            <div className="lg:col-span-8">
              <p className="eyebrow mb-2">{category?.name ?? '影视条目'}</p>
              <h1 className="text-[28px] font-semibold leading-tight tracking-wide text-foreground-950 md:text-[38px]">
                {film.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-foreground-500">
                <span className="flex items-center gap-1 text-primary-400">
                  <i className="ri-star-fill text-[10px]" />
                  {film.rating.toFixed(1)} 分
                </span>
                <span>{film.year}</span>
                <span>{film.region}</span>
                <span>{formatDuration(film.duration)}</span>
                <span>{film.subcategory}</span>
              </div>

              <p className="mt-5 border-l-2 border-primary-500/70 pl-4 text-[14px] leading-7 text-foreground-800">
                {film.summary}
              </p>

              <div className="mt-8 rounded-lg border border-background-300/70 bg-background-100">
                <div className="border-b border-background-300/70 px-5 py-3">
                  <h2 className="flex items-center gap-2 text-[14px] font-medium text-foreground-900">
                    <i className="ri-file-list-3-line text-primary-400" />
                    基本资料
                  </h2>
                </div>
                <dl className="grid gap-x-8 gap-y-0 px-5 py-2 sm:grid-cols-2">
                  {metaRows.map((row) => (
                    <div key={row.label} className="flex gap-3 border-b border-background-300/40 py-3 last:border-0">
                      <dt className="w-16 shrink-0 text-[12px] text-foreground-500">{row.label}</dt>
                      <dd className="min-w-0 flex-1 text-[12px] leading-5 text-foreground-800">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <section className="mt-8">
                <h2 className="mb-3 flex items-center gap-2 text-[15px] font-medium text-foreground-950">
                  <i className="ri-quill-pen-line text-primary-400" />
                  剧情与作品简介
                </h2>
                <p className="text-[13px] leading-8 text-foreground-700">{film.synopsis}</p>
              </section>

              <section className="mt-6">
                <h2 className="mb-3 text-[13px] font-medium text-foreground-900">相关标签</h2>
                <div className="flex flex-wrap gap-2">
                  {film.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md border border-background-300/70 bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </section>

              <section className="mt-6 rounded-lg border border-background-300/70 bg-background-100 p-5">
                <h2 className="mb-3 flex items-center gap-2 text-[13px] font-medium text-foreground-900">
                  <i className="ri-folder-open-line text-primary-400" />
                  所属分类
                </h2>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to={`/category/${film.category}`}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-500/40 bg-primary-500/10 px-3 py-1.5 text-[12px] text-primary-400 transition-colors hover:bg-primary-500/20"
                  >
                    {category?.name ?? '分类'}
                    <i className="ri-arrow-right-line text-[12px]" />
                  </Link>
                  <Link
                    to={`/category/${film.category}?sub=${encodeURIComponent(film.subcategory)}`}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-[12px] text-foreground-700 transition-colors hover:border-primary-500/60 hover:text-primary-400"
                  >
                    {film.subcategory}
                  </Link>
                </div>
              </section>
            </div>
          </div>

          <section className="mt-16">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="eyebrow mb-2">Related</p>
                <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">相关推荐</h2>
                <p className="mt-2 text-[12px] leading-6 text-foreground-500">
                  依据分类、子分类与标签的相似度匹配，作为内容发现的演示。
                </p>
              </div>
              <Link
                to={`/category/${film.category}`}
                className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-[12px] text-foreground-700 transition-colors hover:border-primary-500/60 hover:text-primary-400"
              >
                更多同类内容
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4">
              {recommended.map((item, index) => (
                <FilmCard key={item.id} film={item} index={index} />
              ))}
            </div>
          </section>
        </article>
      </main>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 animate-rise rounded-md border border-primary-500/40 bg-background-100 px-4 py-3 text-[12px] text-foreground-900">
          {toast}
        </div>
      )}

      <SiteFooter />
    </div>
  );
}